import {setWith} from 'lodash-es';
import {
	type CommonAllEnumeratorsResult,
	GsoaGastroService,
	type MenuResultCollectionResult,
	type OfferMealTypeResultCollectionResult,
} from '~/openapi';
import type {ProductionTypeIdEnum} from '~/stores/shoppingCart';
import {GASTRO_FRONTEND_ID} from '#shared/constants';

type FetchOffersParams = {
	localityId: number;
	productTypeId: ProductionTypeIdEnum;
	limit: number;
	offset: number;
	frontendId?: number;
	onlyOrderableNow?: boolean;
	onlyOrderableToday?: boolean;
	tags?: string[];
	offerIds?: string[];
};

type ValidateCartParams = {
	localityId: number,
	offerIds: number[],
	productTypeId: ProductionTypeIdEnum
};

export const useGoosGastroStore = defineStore('goosGastro', () => {
	const offers = ref<Record<string, Record<string, OfferMealTypeResultCollectionResult>>>({});
	const menu = ref<Record<string, MenuResultCollectionResult['result']>>({});
	const enumerators = ref<CommonAllEnumeratorsResult | null>(null);

	const restaurantMealTypes = computed(() => {
		return enumerators.value?.mealTypes?.filter((type) => type.id && type.id <= 99);
	});

	const butcheryMealTypes = computed(() => {
		return enumerators.value?.mealTypes?.filter((type) => type.id && type.id >= 200 && type.id < 300);
	});

	async function fetchOffers(
		{
			frontendId = GASTRO_FRONTEND_ID,
			onlyOrderableNow = true,
			...restParams
		}: FetchOffersParams,
	) {
		const params = {
			frontendId,
			onlyOrderableNow,
			...restParams,
		};

		try {
			const data = await cancelTimeout(() => GsoaGastroService.getMealOffersMealType(params));
			if (data) {
				const cacheKey = `${restParams.localityId}_${restParams.productTypeId}_${restParams.limit || 0}`;
				setWith(offers.value, cacheKey, data, Object);
				return data;
			}
			return null;
		} catch (e: any) {
			throw createError({
				message: 'Failed to fetch meal offers',
				data: params,
				cause: e,
			});
		}
	}

	async function validateCart({localityId, offerIds, productTypeId}: ValidateCartParams) {
		const params = {
			frontendId: GASTRO_FRONTEND_ID,
			localityId,
			productTypeId,
			requestBody: offerIds,
		};

		try {
			const {result} = await cancelTimeout(() => GsoaGastroService.cartValidate(params));

			return result;
		} catch (e: any) {
			throw createError({
				message: 'Failed to validate cart',
				data: params,
				cause: e,
			});
		}
	}

	async function fetchEnumerators() {
		if (enumerators.value) {
			return enumerators.value;
		}

		try {
			const {result} = await cancelTimeout(() => GsoaGastroService.commonGetAllEnumerators({}));
			if (result) {
				enumerators.value = result;
			}

			return result;
		} catch (e: any) {
			throw createError({
				message: 'Failed to fetch enumerators',
				cause: e,
			});
		}
	}

	return {
		menu,
		offers,
		enumerators,
		restaurantMealTypes,
		butcheryMealTypes,
		fetchOffers,
		validateCart,
		fetchEnumerators,
	};
});
